import React from "react";
import cns from "classnames";
import { darken } from "polished";
import Typography, { TypographyProps } from "@material-ui/core/Typography";

import OpenInNewIcon from "react-icons/lib/md/open-in-new";
import CallIcon from "@material-ui/icons/Call";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";

import { ThemeType } from "@theme";
import CopyIcon from "../CopyIcon";
import { makeStyles } from "@material-ui/core/styles";

type Props = Partial<TypographyProps> & {
  className?: string;
  inline?: boolean;
  target?: "_blank";
  dashed?: boolean;
  size?: "medium" | "small";
  withIcon?: boolean;
  copy?: boolean;
  [otherProps: string]: any;
};

const hrefTypes = ["tel:", "mobilnum:", "mailto:"];

/** @deprecated use WithCopyToClipboard instead */
export function PlainLink(props: Props) {
  const { children, className, dashed, inline, size, withIcon, copy, ...rest } = props;
  const classes = useStyles({});
  const href = rest.href;
  // not empty tel
  const isTel = href && href.includes("tel:") && href !== "tel:";

  // not empty mobilnum
  const isMobil = href && href.includes("mobilnum:") && href !== "mobilnum:";

  // not empty email
  const isMail = href && href.includes("mailto:") && href !== "mailto:";

  // open in new window
  const isBlank = rest.target === "_blank";
  const classNames = cns(classes.root, className, {
    [classes.inline]: inline,
    [classes.dashed]: dashed,
    [classes.small]: size === "small",
    [classes.nowrap]: isTel || isMail || isMobil
  });

  return (
    <Typography
      className={classNames}
      component={"a" as any}
      {...rest}
      onClick={(e) => {
        if (copy) {
          e.stopPropagation();
          if (!hrefTypes.some((x) => href?.includes(x))) {
            e.preventDefault();
          }
          navigator.clipboard.writeText(e.target.getAttribute("href")).then(
            () => {
              console.log("clipboard successfully set");
            },
            () => {
              console.log("clipboard fail");
            }
          );
        } else {
          rest.onClick && rest.onClick(e);
        }
      }}
    >
      {isTel && withIcon && <CallIcon fontSize="inherit" className={classes.icon} />}
      {isMobil && withIcon && (
        <PhoneIphoneIcon fontSize="inherit" className={classes.icon} />
      )}
      {isMail && withIcon && <EmailIcon fontSize="inherit" className={classes.icon} />}
      {children}
      {copy && (
        <CopyIcon
          className={"copy"}
          value={
            isTel
              ? href.replace("tel:", "")
              : isMail
              ? href.replace("mailto:", "")
              : children
              ? children.toString()
              : ""
          }
        />
      )}
      {isBlank && <OpenInNewIcon />}
    </Typography>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    transition: theme.transitions.create("color"),

    "& > .copy": {
      position: "absolute",
      zIndex: 10,
      opacity: 0,
      transition: "opacity .25s ease-in-out",
      marginLeft: theme.spacing(),
      float: "right",
      display: "inline-flex",
      marginTop: 3
    },

    "&:hover, &:active": {
      color: darken(0.1, theme.palette.primary.main),
      "& > .copy": {
        opacity: 1
      }
    }
  },

  inline: {
    alignItems: "center",
    display: "inline-flex"
  },

  dashed: {
    borderBottom: `1px dashed ${theme.palette.secondary.main}`
  },

  small: {
    fontSize: theme.typography.fontSize * 0.85
  },

  nowrap: {
    whiteSpace: "nowrap"
  },

  icon: {
    position: "relative",
    marginRight: 4
  }
}));
